import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { type NavigateFunction, type useLocation } from 'react-router'
import { type Blocker, type SetURLSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import localDate, { DayjsFormats } from 'shared/lib/dayjs'
import { IS_APP_LIGHT, IS_CALLING_APP_LIGHT, NODE_ENV } from 'shared/config'
import { truthy } from 'shared/lib'
import type { IParamsChangeRoute, IParamsChangeRouteVue } from './types'

type RouterLocation = ReturnType<typeof useLocation>

class UIStore {
  constructor() {
    makeAutoObservable(this)
    this.initScrolling()
    reaction(
      () => this.pathName,
      (pathName, prev) => {
        this.prevPathName = prev
      }
    )
    setInterval(() => {
      runInAction(() => {
        this._offsetMinute = this.dayjs().utcOffset() - dayjs().utcOffset()
      })
    }, 1000)
  }
  customPathLabel: { [key: string]: string } = {
    '/contacts': 'All contacts',
    '/broadcasts/all': 'All broadcasts',
    '/broadcasts/sms': 'SMS broadcasts',
    '/broadcasts/ringless': 'Ringless broadcasts',
    '/broadcasts/view': 'All broadcasts',
    '/triggers/all': 'All triggers',
    '/triggers/sms': 'SMS triggers',
    '/triggers/ringless': 'Ringless triggers',
    '/triggers/view': 'All triggers',
    '/settings/integrations': 'App Marketplace',
    '/keywords/view': 'All keywords',
    '/power-dialer': 'Power Dialer campaigns',
    '/analytics/conversions': 'Conversions',
    '/settings/members': 'Members',
  }
  navigate: NavigateFunction | null = null
  location: RouterLocation | null = null
  pathName = ''
  queryParams = ''
  prevPathName = ''
  playAudioId = ''
  globalAlerts = []
  private _timezone: string | null = null
  routeBlocker: Blocker | null = null

  scrolling = false

  isFocusingMode = false

  idScrollingTimeout: NodeJS.Timer | null = null

  searchParams: URLSearchParams | null = null
  setSearchParams: SetURLSearchParams | null = null

  get debugMode() {
    const query = Object.fromEntries(new URLSearchParams(location.search))

    return query.debug === '1'
  }

  get timezone() {
    if (NODE_ENV !== 'production' && this._timezone === null && this.debugMode) {
      console.warn('Empty user timezone')
    }
    return this._timezone ?? dayjs.tz.guess()
  }

  get timezoneShort() {
    return this.dayjs().format(DayjsFormats.timezone)
  }

  get timezoneUTC() {
    return this.dayjs().format(DayjsFormats.UTCOffset)
  }

  private _offsetMinute = 0

  get offsetMinute() {
    return this._offsetMinute
  }

  startFocusingMode = () => {
    this.isFocusingMode = true
  }

  endFocusingMode = () => {
    this.isFocusingMode = false
  }

  initScrolling = () => {
    const onScroll = () => {
      if (this.scrolling) {
        return
      }
      this.idScrollingTimeout && clearTimeout(this.idScrollingTimeout)
      runInAction(() => {
        this.scrolling = true
      })
      this.idScrollingTimeout = setTimeout(() => {
        runInAction(() => {
          this.scrolling = false
        })
      }, 1000)
    }
    const onScrollEnd = () => {
      this.idScrollingTimeout && clearTimeout(this.idScrollingTimeout)
      runInAction(() => {
        this.scrolling = false
      })
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('scrollend', onScrollEnd, true)
  }

  dayjs(date?: string | number | Date | dayjs.Dayjs | null | undefined, useTimezone = true) {
    if (!useTimezone) return dayjs(date)
    if (this.timezone) return localDate(date, this.timezone)

    return localDate(date)
  }

  dayjsWithoutTimezone(date?: string | number | Date | dayjs.Dayjs | null | undefined) {
    return this.dayjs(date, false)
  }

  setNavigate = (navigate: NavigateFunction) => {
    this.navigate = navigate
  }

  setPlayAudioId = (id: string) => {
    this.playAudioId = id
  }

  setTimezone = (timezone: string | null) => {
    this._timezone = timezone
  }

  checkRouteType = (page: string) => {
    if (!window.DesignV2API) return 'default'

    page = page[0] === '/' ? page.replace('/', '') : page

    return window.DesignV2API.handleCheckPage(page) ? 'default' : 'vue'
  }

  requiredQueryParams: URLSearchParams = new URLSearchParams()
  setRequiredQueryParams = (requiredQueryParams: typeof this.requiredQueryParams) => {
    this.requiredQueryParams = requiredQueryParams
  }

  changeQueryRoute = (query: string) => {
    const location = this.location ? this.location : window.location

    this.changeRoute({
      method: 'replace',
      path: location.pathname,
      query: query,
    })
  }

  changeRoute({
    path,
    name,
    query,
    type = 'default',
    method = 'push',
    options,
  }: IParamsChangeRoute) {
    const pathSplit = path.split('?')
    const pathOrigin = pathSplit[0]
    const pathQuery = pathSplit[1]
    const queryProps = [query, pathQuery].filter(truthy).join('&')

    const externalQueryParams = new URLSearchParams(queryProps)
    const allParams = new URLSearchParams({
      ...Object.fromEntries(externalQueryParams),
      ...Object.fromEntries(this.requiredQueryParams),
    })

    const queryString = allParams.toString()

    const newPath = queryString ? `${pathOrigin}?${queryString}` : path

    if (type === 'default' || IS_APP_LIGHT || IS_CALLING_APP_LIGHT) {
      if (this.navigate) {
        this.navigate(newPath, {
          replace: method === 'replace',
          ...options,
        })
      }
      if (IS_APP_LIGHT || IS_CALLING_APP_LIGHT) {
        return
      }
    }

    if (type === 'vue') {
      if (this.routeBlocker) {
        if (this.navigate) {
          this.navigate(newPath, options)
        }

        return
      }

      if (window.RouterVue) {
        const queryVue: Record<string, string> = {}

        for (const [key, value] of allParams.entries()) {
          queryVue[key] = value
        }

        // TODO: https://github.com/vuejs/router/issues/366
        // will be removed after uninstalling vue
        window.RouterVue['replace'](
          name
            ? {
                name: name,
                query: queryVue,
                params: {
                  state: options?.state,
                },
              }
            : {
                path: pathOrigin,
                query: queryVue,
              }
        )
      }
    }
  }

  changeRouteViaVue = (params: IParamsChangeRoute) => {
    if (window.DesignV2API && this.location) {
      const fromType = this.checkRouteType(this.location.pathname)
      const toType = this.checkRouteType(params.path)

      this.changeRoute({
        ...params,
        type: fromType === 'vue' ? fromType : toType,
      })
    } else {
      this.changeRoute(params)
    }
  }

  changeRouteOnlyVue = (method: 'push' | 'replace', params: IParamsChangeRouteVue) => {
    if (window.RouterVue) {
      window.RouterVue[method](params)
    }
  }

  setLocation = (location: RouterLocation) => {
    this.location = location
    this.setPathName(location.pathname)
    this.setQueryParams(location.search)
  }

  setPathName = (pathName: string) => {
    this.pathName = pathName
  }

  setQueryParams = (query: string) => {
    this.queryParams = query
  }

  setRoutesBlocker = (blocker: Blocker | null) => {
    this.routeBlocker = blocker
  }

  setCustomPathLabel = (path: string, label: string) => {
    this.customPathLabel[path] = label
  }

  setSearchParamsToUIStore = (searchParams: URLSearchParams) => {
    this.searchParams = searchParams
  }
  setSearchParamsSetterToUIStore = (setSearchParams: SetURLSearchParams) => {
    this.setSearchParams = setSearchParams
  }
}

export const uiStore = new UIStore()
